import * as React from 'react';
import { EVENT_LISTENERS, TRANSFER_FLAG, USER_DATA } from '../constants/constants';
import FormItem from '../form/FormItem';
import FormWidget from '../form/FormWidget';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { C, H, V } from '../Layout';
import { useStyling } from '../Theme';
import useHistory from '../utils/useHistory';
import { trackInitiatingGoogleSignIn } from './authNetworkLogger';
import { buildGoogleSocialSigninURI } from './ssoAuth';

interface UserData {
    email?: string;
    tp?: number;
    professionalRole?: string;
}

export default ({
    valid,
    email,
    setEmail,
    hasErrors,
    keyboardEventHandler,
    setValid,
    validator,
    emailFromReferrer
}) => {
    const { location } = useHistory();
    const { theme } = useStyling();
    const emailRef = React.useRef<HTMLInputElement>(null);

    const userData: UserData = React.useMemo(() => {
        const rawUserData = localStorage.getItem(USER_DATA);

        const TRANSFER_IN_PROGRESS = localStorage.getItem(TRANSFER_FLAG);
        let parsed: UserData = {};

        if (rawUserData) {
            try {
                parsed = JSON.parse(rawUserData);
            } catch (e) {
                console.error('Failed to parse USER_DATA or tp', e);
            }
        }

        if (TRANSFER_IN_PROGRESS) {
            localStorage.removeItem(TRANSFER_FLAG);
        }

        return parsed;
    }, []);

    const presetEmail = userData?.email ?? location.state?.email ?? location.state?.username ?? emailFromReferrer;

    const validate = React.useCallback(
        (username) => {
            const validation = validator.validate({ username });
            setValid({ errors: validation.errors, isValid: validation.isValid });
            setEmail(presetEmail || username);
        },
        [presetEmail, setEmail, setValid, validator]
    );

    React.useEffect(() => {
        if (presetEmail) {
            validate(presetEmail);
        }
    }, [presetEmail, validate]);

    const handleGoogleLogin = () => {
        const googleSigninURI = buildGoogleSocialSigninURI();
        trackInitiatingGoogleSignIn(googleSigninURI);
        window.location.href = googleSigninURI;
    };

    const allAuthOptions = {
        contacts: {
            google: {
                iconComponent: <Icons.GoogleLogo sx={{ width: '32px', height: '32px' }} />,
                label: <I18n token="dialog.google.login" />,
                handleLogin: handleGoogleLogin
            }
        }
    };

    React.useEffect(() => {
        const handleUnload = () => {
            const TRANSFER_IN_PROGRESS = localStorage.getItem(TRANSFER_FLAG);
            if (!TRANSFER_IN_PROGRESS) {
                localStorage.removeItem(USER_DATA);
            }
        };

        window.addEventListener(EVENT_LISTENERS.beforeunload, handleUnload);
        return () => window.removeEventListener(EVENT_LISTENERS.beforeunload, handleUnload);
    }, []);

    return (
        <FormItem>
            <V gap={2} fill>
                <H fill gap={2} sx={{ justifyContent: 'center' }}>
                    {Object.keys(allAuthOptions?.contacts).map((key) => {
                        const { iconComponent: Icon, handleLogin } = allAuthOptions.contacts[key];
                        return (
                            <H
                                key={key}
                                onClick={handleLogin}
                                sx={{
                                    padding: 1,
                                    display: 'flex',
                                    cursor: 'pointer',
                                    borderRadius: 0.5,
                                    alignItems: 'center',
                                    width: 'fit-content',
                                    justifyContent: 'center',
                                    border: `1px solid ${theme.palette.divider}`
                                }}
                            >
                                {Icon}
                            </H>
                        );
                    })}
                </H>
                <H fill>
                    <C sx={{ flex: 1, borderBottom: `1px solid ${theme.palette.divider}` }} />
                    <C sx={{ margin: '0 8px', fontWeight: 'bold' }}>
                        <I18n token="dialog.or" />
                    </C>
                    <C sx={{ flex: 1, borderBottom: `1px solid ${theme.palette.divider}` }} />
                </H>

                <FormWidget
                    name="username"
                    label={<I18n token="account.form.email" />}
                    value={presetEmail ?? email}
                    errors={valid?.errors?.username}
                    hasErrors={hasErrors('username')}
                    onChange={() => validate(emailRef.current?.value ?? '')}
                    onKeyUp={(e) => keyboardEventHandler(e, email)}
                    disabled={!!presetEmail || !!userData?.email}
                    ref={emailRef}
                />
            </V>
        </FormItem>
    );
};
